import React, { ReactNode, RefObject, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type TooltipPlacement = 'right' | 'below';

const GAP = 8;
const PAD = 8;

function clamp(value: number, min: number, max: number): number {
    if (max < min) {
        return min;
    }
    return Math.min(Math.max(value, min), max);
}

export function clampTooltipPosition(anchor: HTMLElement, panel: HTMLElement, preferred: TooltipPlacement): void {
    const wrap = anchor.closest('.consume-grid-wrap');
    const bounds = (wrap instanceof HTMLElement ? wrap : document.documentElement).getBoundingClientRect();
    const anchorBox = anchor.getBoundingClientRect();
    const maxW = Math.max(120, bounds.width - PAD * 2);
    const maxH = Math.max(80, bounds.height - PAD * 2);

    const computedMax = parseFloat(window.getComputedStyle(panel).maxWidth);
    const widthCap = Number.isFinite(computedMax) ? Math.min(maxW, computedMax) : maxW;
    panel.style.position = 'fixed';
    panel.style.right = 'auto';
    panel.style.bottom = 'auto';
    panel.style.transform = 'none';
    panel.style.minWidth = '0px';
    panel.style.maxWidth = `${Math.round(widthCap)}px`;
    panel.style.maxHeight = `${Math.round(maxH)}px`;
    panel.style.overflowY = 'auto';

    const width = panel.offsetWidth;
    const height = panel.offsetHeight;
    const minX = bounds.left + PAD;
    const maxX = bounds.right - PAD - width;
    const minY = bounds.top + PAD;
    const maxY = bounds.bottom - PAD - height;

    let left = 0;
    let top = 0;

    if (preferred === 'right') {
        const rightX = anchorBox.right + GAP;
        const leftX = anchorBox.left - GAP - width;
        const fitsRight = rightX + width <= bounds.right - PAD + 0.5;
        const fitsLeft = leftX >= bounds.left + PAD - 0.5;
        if (fitsRight || (!fitsLeft && bounds.right - anchorBox.right >= anchorBox.left - bounds.left)) {
            left = rightX;
        } else {
            left = leftX;
        }
        top = anchorBox.top + (anchorBox.height - height) / 2;
    } else {
        const belowY = anchorBox.bottom + GAP;
        const aboveY = anchorBox.top - GAP - height;
        const fitsBelow = belowY + height <= bounds.bottom - PAD + 0.5;
        const fitsAbove = aboveY >= bounds.top + PAD - 0.5;
        if (fitsBelow || (!fitsAbove && bounds.bottom - anchorBox.bottom >= anchorBox.top - bounds.top)) {
            top = belowY;
        } else {
            top = aboveY;
        }
        left = anchorBox.left;
    }

    left = clamp(left, minX, maxX);
    top = clamp(top, minY, maxY);
    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;
}

export function useClampedTooltip<T extends HTMLElement = HTMLElement>(
    preferred: TooltipPlacement
): {
    open: boolean;
    anchorRef: RefObject<T>;
    panelRef: RefObject<HTMLDivElement>;
    anchorProps: {
        onMouseEnter: () => void;
        onMouseLeave: () => void;
        onFocus: () => void;
        onBlur: (event: React.FocusEvent<T>) => void;
    };
} {
    const anchorRef = useRef<T>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);

    useLayoutEffect(() => {
        if (!open) {
            return;
        }
        const anchor = anchorRef.current;
        if (!anchor) {
            return;
        }

        const place = () => {
            const panel = panelRef.current;
            if (!panel) {
                return;
            }
            clampTooltipPosition(anchor, panel, preferred);
        };

        place();
        const frame = window.requestAnimationFrame(place);
        const wrap = anchor.closest('.consume-grid-wrap');
        wrap?.addEventListener('scroll', place, { passive: true });
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, { passive: true, capture: true });
        const observer = panelRef.current ? new ResizeObserver(place) : null;
        if (panelRef.current && observer) {
            observer.observe(panelRef.current);
        }

        return () => {
            window.cancelAnimationFrame(frame);
            wrap?.removeEventListener('scroll', place);
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
            observer?.disconnect();
        };
    }, [open, preferred]);

    return {
        open,
        anchorRef,
        panelRef,
        anchorProps: {
            onMouseEnter: () => {
                setOpen(true);
            },
            onMouseLeave: () => {
                setOpen(false);
            },
            onFocus: () => {
                setOpen(true);
            },
            onBlur: (event: React.FocusEvent<T>) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    setOpen(false);
                }
            },
        },
    };
}

export const BoundTooltip: React.FC<{
    open: boolean;
    panelRef: RefObject<HTMLDivElement>;
    className: string;
    children: ReactNode;
}> = ({ open, panelRef, className, children }) => {
    if (!open || typeof document === 'undefined') {
        return null;
    }
    return createPortal(
        <div ref={panelRef} className={`${className} is-open`} role="tooltip">
            {children}
        </div>,
        document.body
    );
};
